import React from 'react';
import { FadeIn } from './ui/FadeIn';
import { Leaf } from 'lucide-react';
export function SalesCopy() {
  return <section className="py-10 md:py-20 px-6 max-w-2xl mx-auto" data-id="element-111">
      {/* Opening Hook */}
      <FadeIn data-id="element-112">
        <div className="space-y-5 md:space-y-8 text-lg text-stone-700 leading-relaxed" data-id="element-113">
          <p className="text-xl md:text-2xl font-serif italic text-olive-800" data-id="element-114">
            You eat the kale. You hit the gym. You take the supplements.
          </p>

          <p data-id="element-115">
            But every night, your head hits the pillow and{' '}
            <strong className="text-olive-900" data-id="element-116">your mind starts racing</strong>.
            The invisible weight of modern stress, digital noise, and the
            "never-enough" culture is doing something your diet can't fix:{' '}
            <em className="text-olive-800 font-medium" data-id="element-117">
              it's aging you from the inside out.
            </em>
          </p>

          <p data-id="element-118">
            In the Blue Zones—places where people routinely live to 100 with
            their minds as sharp as tacks—they don't have "wellness apps" or
            "biohacking seminars."
          </p>

          <p className="font-medium text-olive-900 text-xl border-l-4 border-gold-500 pl-6 my-4 italic font-serif" data-id="element-119">
            They have something better. They have a Mindset.
          </p>
        </div>
      </FadeIn>

      {/* Transition — clarity promise */}
      <FadeIn delay={0.1} data-id="element-120">
        <div className="mt-8 md:mt-14 mb-4 md:mb-6 space-y-5 md:space-y-7 text-lg text-stone-700 leading-relaxed" data-id="element-121">
          <p data-id="element-122">
            But before we go further, let me be clear:{' '}
            <strong className="text-olive-900" data-id="element-123">
              This isn't a 300-page textbook on ancient history.
            </strong>{' '}
            You don't have time for that.
          </p>

          <p data-id="element-124">
            In the next <strong className="text-medBlue-700" data-id="element-125">3 minutes</strong>,
            I'm going to show you why your brain is currently wired to{' '}
            <em className="font-medium text-olive-800" data-id="element-126">'self-destruct'</em>{' '}
            under modern pressure—and the exact{' '}
            <strong className="text-medBlue-700" data-id="element-127">60-second 'anchor'</strong> you
            will use to stop the mental noise before your head hits the pillow
            tonight.
          </p>

          <p className="text-xl font-serif text-olive-900 font-medium py-2" data-id="element-128">
            If you're looking for a way to turn off the 'racing mind' and
            reclaim your energy by tomorrow morning, keep reading.
          </p>
        </div>
      </FadeIn>

      <div className="flex justify-center my-10 md:my-20" data-id="element-129">
        <Leaf className="text-olive-300 w-8 h-8" data-id="element-130" />
      </div>

      {/* The Letter Body */}
      <FadeIn delay={0.2} data-id="element-131">
        <div className="space-y-5 md:space-y-8 text-lg text-stone-700 leading-relaxed" data-id="element-132">
          <p className="text-sm text-stone-400 uppercase tracking-[0.2em] mb-1 text-center" data-id="element-133">
            The Invisible Barrier to Your Longest Life
          </p>
          <p className="font-serif text-2xl md:text-3xl text-olive-900 mb-4 text-center" data-id="element-134">
            Dear Friend,
          </p>

          <div className="w-12 h-px bg-olive-200 mx-auto mb-4" data-id="element-135" />

          <p data-id="element-136">
            We've been lied to. We've been told that longevity is a battle of{' '}
            <strong className="text-olive-900" data-id="element-137">calories and cardio.</strong>
          </p>

          <p data-id="element-138">
            But if you visit the mountain villages of Sardinia or the coast of
            Costa Rica, you'll see something the health industry ignores. You'll
            see{' '}
            <strong className="text-olive-900" data-id="element-139">
              90-year-olds with more joy and mental peace
            </strong>{' '}
            than most 30-year-olds in New York or London.
          </p>

          <p className="font-serif text-xl text-olive-800 italic py-1 md:py-2" data-id="element-140">
            The difference isn't just the air or the olive oil.
          </p>

          {/* Cognitive Anchor reveal */}
          <div className="py-4 my-2 md:py-6 md:my-4 border-y border-olive-100" data-id="element-141">
            <h3 className="text-2xl md:text-3xl font-serif text-medBlue-700 text-center" data-id="element-142">
              It's their "Cognitive Anchor."
            </h3>
          </div>

          <p data-id="element-143">
            The modern world is designed to keep your nervous system in a state
            of <strong className="text-olive-900" data-id="element-144">high alert</strong>. This
            constant "red-line" state creates inflammation, destroys sleep, and
            literally{' '}
            <em className="font-medium text-olive-800" data-id="element-145">
              shortens your telomeres
            </em>{' '}
            (the caps on your DNA).
          </p>
        </div>
      </FadeIn>

      {/* Emotional deepening — the 3AM moment */}
      <FadeIn delay={0.3} data-id="element-146">
        <div className="mt-10 md:mt-16 space-y-5 md:space-y-8 text-lg text-stone-700 leading-relaxed" data-id="element-147">
          <p className="text-xl font-serif text-olive-900" data-id="element-148">Think about it.</p>

          <p data-id="element-149">
            How many nights have you stared at the ceiling at{' '}
            <strong className="text-medBlue-700" data-id="element-150">3:00 AM</strong>, your jaw
            clenched, your chest tight, replaying a 10-second conversation from
            earlier that day?
          </p>

          <p data-id="element-151">
            You feel the weight of{' '}
            <em className="font-medium text-olive-800" data-id="element-152">'never enough.'</em> You
            fear that if you slow down, you'll lose your edge.
          </p>

          <p className="text-xl font-serif font-medium text-olive-900 border-l-4 border-gold-500 pl-6 my-2 md:my-4 italic" data-id="element-153">
            But the truth is, you are losing your life.
          </p>

          <p data-id="element-154">
            Every hour your nervous system stays locked in this 'survival mode,'
            you aren't just tired—
            <strong className="text-olive-900" data-id="element-155">
              you are biologically eroding.
            </strong>{' '}
            You're snapping at your kids, fading at your desk, and losing that
            'spark' that used to make you unstoppable.
          </p>

          <p className="text-xl font-medium text-olive-900 py-1 md:py-2" data-id="element-156">
            The 'healthy' habits you're doing are just putting a band-aid on a
            house fire.{' '}
            <span className="text-medBlue-700" data-id="element-157">
              It's time to put out the fire.
            </span>
          </p>

          <div className="flex justify-center py-2 md:py-4" data-id="element-158">
            <div className="w-16 h-px bg-olive-200" data-id="element-159" />
          </div>

          <p className="text-center text-lg text-stone-700" data-id="element-160">
            That's why I created the
          </p>
          <h3 className="text-2xl md:text-4xl font-serif text-olive-900 text-center leading-tight py-1 md:py-2" data-id="element-161">
            Blue Zone Longevity{' '}
            <span className="italic text-medBlue-700" data-id="element-162">Mindset Guide</span>
          </h3>

          <div className="flex justify-center py-4 md:py-8" data-id="element-163">
            <img src="https://cdn.magicpatterns.com/uploads/e4Nn1zvqHur2UMBLWLy9yC/BZ-mockup.png" alt="Blue Zone Longevity Mindset Guide" className="w-full max-w-xs md:max-w-sm drop-shadow-2xl rounded-lg" data-id="element-164" />
          </div>

          <p data-id="element-165">
            This is the first hybrid program designed to bridge the gap between{' '}
            <strong className="text-olive-900" data-id="element-166">ancient elder wisdom</strong> and
            your modern, chaotic reality. We've decoded the specific mental
            frameworks—like <strong className="text-medBlue-700" data-id="element-167">Ikigai</strong>{' '}
            (Purpose) and <strong className="text-medBlue-700" data-id="element-168">Moai</strong>{' '}
            (Connection)—and turned them into a practical,{' '}
            <strong className="text-olive-900" data-id="element-169">5-minute-a-day system</strong>.
          </p>
        </div>
      </FadeIn>
    </section>;
}